import React, { createContext, useEffect, useRef, useState, useCallback } from 'react';
import * as api from '../lib/api.js';
import * as utils from '../lib/utils.js';
import { BUILD_DATE } from '../lib/buildInfo.js';
import { navigate } from 'gatsby';

// Context consumers can call playTrack(node) and read auth/playback state
export const AppShellContext = createContext({
	playTrack: () => {},
	playbackDescription: '',
	userInfo: null,
	isUserAuthenticated: false,
});

export default function PersistentLayout({ children }) {
	const [userInfo, setUserInfo] = useState(null);
	const [isUserAuthenticated, setIsUserAuthenticated] = useState(false);
	const [isLoadingAudio, setIsLoadingAudio] = useState(false);
	const [playbackDescription, setPlaybackDescription] = useState('');
	const audioRef = useRef(null);
	const [isAudioPlayerVisible, setIsAudioPlayerVisible] = useState(false);
	const [serverConnected, setServerConnected] = useState(true);

	const formattedBuildDate = new Date(BUILD_DATE).toLocaleString();

	// replace isLoadingAudio usage with a ref-backed version so playTrack identity can be stable
	const isLoadingAudioRef = useRef(false);
	const setIsLoadingAudioSafe = (val) => {
		isLoadingAudioRef.current = val;
		setIsLoadingAudio(val);
	};

	// new refs to guard against duplicate onended handling / reentrancy
	const playbackIdRef = useRef(0);
	const isNavigatingRef = useRef(false);

	// Init app: check server and fetch user info once on mount
	useEffect(() => {
		let mounted = true;
		async function init() {
			const connected = await api.CheckRestApiConnection();
			if (!mounted) return;
			setServerConnected(connected);
			if (connected) {
				const fetchedUserInfo = await api.getUserInfo();
				const isAuthed =
					!!fetchedUserInfo &&
					(!!fetchedUserInfo.email || !!fetchedUserInfo.username || !!fetchedUserInfo.name);
				if (isAuthed) {
					setUserInfo(fetchedUserInfo);
					setIsUserAuthenticated(true);
				} else {
					setUserInfo(null);
					setIsUserAuthenticated(false);
				}
			} else {
				setUserInfo(null);
				setIsUserAuthenticated(false);
			}
		}
		init();
		return () => {
			mounted = false;
		};
	}, []);

	// playTrack is the single source of truth for audio playback
	const playTrack = useCallback(
		async (nodeToPlay, options = {}) => {
			const audioPlayer = audioRef.current;
			if (!nodeToPlay || !audioPlayer) return;

			// Increment playback id for this session and capture locally
			const currentPlaybackId = ++playbackIdRef.current;

			// cancel any previous onended handler to avoid duplicates
			try {
				audioPlayer.onended = null;
			} catch (e) {
				// ignore if setting to null fails
			}

			if (isLoadingAudioRef.current) {
				setPlaybackDescription('Waiting for previous track to load...');
				return;
			}

			try {
				setIsLoadingAudioSafe(true);
				setIsAudioPlayerVisible(true);
				setPlaybackDescription('Loading: ' + nodeToPlay.title);

				const url = await api.GetMp3(nodeToPlay.hash);
				if (url) {
					// If another playback started meanwhile, abort this one
					if (currentPlaybackId !== playbackIdRef.current) return;

					audioPlayer.src = url;
					setPlaybackDescription('Playing: ' + nodeToPlay.title);

					// set onended bound to this playback id
					audioPlayer.onended = () => {
						// ignore if playback id no longer matches (another playback started)
						if (currentPlaybackId !== playbackIdRef.current) return;

						// prevent re-entrancy from multiple onended calls
						// bump playback id so any lingering handlers won't run
						playbackIdRef.current++;

						// clear handler to be safe
						try {
							audioPlayer.onended = null;
						} catch (e) {}

						// mark navigation in progress to avoid race conditions
						if (options.nextUrl && !isNavigatingRef.current) {
							isNavigatingRef.current = true;
							// navigate to next page and request autoplay there
							navigate(options.nextUrl + '?autoplay=true');
							return;
						}

						if (typeof options.nextCallback === 'function') {
							try {
								options.nextCallback();
							} catch (e) {
								console.error('nextCallback error', e);
							}
							return;
						}

						setPlaybackDescription('Playback finished.');
					};

					// attempt to play (handle promise)
					try {
						await audioPlayer.play();
					} catch (playErr) {
						// If autoplay is blocked by browser policies, do not keep retrying automatically.
						console.warn('Audio play() promise rejected', playErr);
					}
				} else {
					setPlaybackDescription('Error loading: ' + nodeToPlay.title);
				}
			} catch (err) {
				console.error('playTrack error', err);
				setPlaybackDescription('Error loading: ' + nodeToPlay.title);
			} finally {
				setIsLoadingAudioSafe(false);
				// reset navigation flag after a tick to allow future navigations
				setTimeout(() => {
					isNavigatingRef.current = false;
				}, 200);
			}
		},
		[] // stable identity: uses refs for mutable state
	);

	return (
		<AppShellContext.Provider
			value={{
				playTrack,
				playbackDescription,
				userInfo,
				isUserAuthenticated,
				serverConnected,
			}}
		>
			{/* persistent top login bar */}
			<div className='whole-page'>
				<div className='login-bar'>
					<img src='/logo192.png' className='logo-image' alt='onehistory.io logo' />
					<play-history-logo>onehistory.io</play-history-logo>
					{serverConnected === false ? <div> No Server Connection </div> : <div></div>}
					<build-text> build: {formattedBuildDate} </build-text>

					{isUserAuthenticated && userInfo ? (
						<div className='user-info-display'>
							{userInfo.picture && (
								<img src={userInfo.picture} alt={userInfo.name || userInfo.username || 'User avatar'} className='user-avatar' />
							)}
							<div className='signout-actions'>
								<navigation-button
									className='inverted'
									onClick={() => (window.location.href = utils.ApiUrl() + '/logout')}
								>
									Log out
								</navigation-button>
							</div>
						</div>
					) : (
						<div className='login-actions'>
							<a href='/login/'>
								<navigation-button className='inverted'>Login </navigation-button>
							</a>
						</div>
					)}
				</div>

				{/* main page content (individual pages render here) */}
				<div className='page-content'>{children}</div>

				{/* persistent bottom bar with playback description and audio player */}
				<div className='bottom-bar'>
					<playback-description-text>{playbackDescription}</playback-description-text>
					<div className='player-container'>
						<audio
							className='player custom-audio-style'
							ref={audioRef}
							controls
							controlsList='nodownload noremoteplayback'
							onContextMenu={(e) => e.preventDefault()}
							hidden={!isAudioPlayerVisible}
						>
							<track kind='captions' />
						</audio>
					</div>
				</div>
			</div>
		</AppShellContext.Provider>
	);
}
